import {ConfigurationModel, FeatureFlagEnum} from '../../../../../shared/models/configuration.model';
import {firstValueFrom, of, Subscription, switchMap, tap} from 'rxjs';
import {ConfigurationService} from '../../../../../configuration/configuration.service';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {VehiclesService} from '../../../../../data/vehicles/vehicles.service';
import {ToastService} from '../../../../../shared/services/toast.service';
import {
  StationaryNotificationType,
  VehicleCategoryModel,
  VehicleGroupStationaryConfiguration
} from '../../../../../shared/models/vehicle';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {catchError, debounceTime, filter} from 'rxjs/operators';
import {RootRoute, SettingsRoute} from '../../../../../shared/models/angular-routing';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {CardComponent} from '../../../../../shared/components/card/card.component';
import {
  CardContentDirective,
  CardContentRawDirective
} from '../../../../../shared/components/card/card-content.directive';
import {RowWithToggleComponent} from '../../row-with-toggle/row-with-toggle.component';
import {RowWithSelectComponent} from '../../row-with-select/row-with-select.component';
import {MatError} from '@angular/material/form-field';
import {MatListItem, MatListItemLine, MatListItemMeta, MatListItemTitle, MatNavList} from '@angular/material/list';
import {MatIcon} from '@angular/material/icon';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatDialog} from '@angular/material/dialog';
import {CreateVehicleDialogComponent} from '../create-vehicle-dialog/create-vehicle-dialog.component';
import {VehicleModel} from '../../../../../shared/models/vehicle.model';
import {MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {ElementVisibleDirective} from '../../../../../shared/directives/element-visible.directive';
import {InfiniteScrollDataSource} from '../../../../../shared/tools/InfiniteScrollTools';
import {MatDivider} from '@angular/material/divider';
import {DialogConfirmDeleteVehicleComponent} from '../dialogs-vehicle/dialog-confirm-delete-vehicle.component';
import {VehicleData} from '../model/vehicle.types';
import {
  ConfirmationDialogComponent
} from '../../../../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import {DialogMoveVehicleComponent} from '../dialogs-vehicle/dialog-move-vehicle.component';
import {UserManagementService} from '../../../../../data/users/user-management.service';
import {MatRadioButton, MatRadioGroup} from '@angular/material/radio';
import {MatCheckbox, MatCheckboxChange} from '@angular/material/checkbox';

const STATIONARY_ALERT_LIST_OPTIONS: { id: number, label: string }[] = [
  {id: 15, label: '15 minutes'},
  {id: 20, label: '20 minutes'},
  {id: 30, label: '35 minutes'},
  {id: 45, label: '45 minutes'},
  {id: 60, label: '1 hour'},
];

@Component({
  selector: 'app-vehicle-group-edit',
  templateUrl: './vehicle-group-edit.component.html',
  styleUrls: [
    '../dialogs-vehicle/dialog-components.scss',
    '../../../../settings/settings-fields.scss',
    '../../../../settings/settings-common.scss',
    'vehicle-group-edit.component.scss',
  ],
  imports: [
    ReactiveFormsModule,
    MatProgressSpinner,
    CardComponent,
    CardContentDirective,
    RowWithToggleComponent,
    RowWithSelectComponent,
    MatError,
    MatNavList,
    CardContentRawDirective,
    RouterLink,
    MatListItem,
    MatIcon,
    MatButton,
    MatListItemMeta,
    MatListItemTitle,
    MatListItemLine,
    MatIconButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    ElementVisibleDirective,
    MatDivider,
    MatRadioGroup,
    MatRadioButton,
    MatCheckbox
  ]
})
export class VehicleGroupEditComponent implements OnInit, OnDestroy {
  configuration: ConfigurationModel;
  isCartegraphEnabled = false;

  isLoadingGroup = false;

  protected STATIONARY_ALERT_LIST_OPTIONS = STATIONARY_ALERT_LIST_OPTIONS;
  StationaryNotificationType = StationaryNotificationType;

  notificationUsers: { email: string, selected: boolean }[] = [];
  invalidRecipients: string[] = [];

  state: {
    isImported?: boolean;
    maxStationaryTime?: number;
    allowReportObservations?: boolean;
  } = {
    isImported: false
  };
  vehicleGroup: VehicleCategoryModel;
  formGroup: FormGroup;

  subscriptions: Subscription[] = [];

  isSaving = false;
  saveError: any;

  vehicleDataSource: InfiniteScrollDataSource<VehicleModel>;
  RootRoute = RootRoute;
  SettingsRoute = SettingsRoute;
  private groupId: number;

  constructor(
    private readonly configurationService: ConfigurationService,
    private readonly toastService: ToastService,
    private readonly vehicleService: VehiclesService,
    private readonly userService: UserManagementService,
    private readonly dialog: MatDialog,
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
  ) {
    this.loadGeneralConfiguration();
    this.createFormGroup();
    this.vehicleDataSource = new InfiniteScrollDataSource(
      pageNumber => this.vehicleService.getVehicles(this.groupId, pageNumber)
    );
  }

  ngOnInit(): void {
    this.groupId = +this.activatedRoute.snapshot.paramMap.get('id');
    this.loadGroupData().then();
    this.vehicleDataSource.reload();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  navigateBack() {
    const shouldNavigate = this.formGroup.invalid ?
      firstValueFrom(this.dialog.open(ConfirmationDialogComponent, {
        data:
          {
            title: 'Confirm discarding of unsaved changes',
            text: 'The form contains invalid data and cannot be saved. By leaving you\'ll lose any changes. Are you sure?',
            yesLabel: 'Yes, discard the changes'
          }
      }).afterClosed()) : Promise.resolve(true);

    shouldNavigate.then(navigate => {
      if (navigate) {
        this.router.navigate([`/${RootRoute.SETTINGS}`, SettingsRoute.MANAGE_VEHICLES])
          .then();
      }
    });
  }

  addVehicle() {
    const ref = this.dialog.open(CreateVehicleDialogComponent, {
      minWidth: 600,
      data: {
        groupId: this.groupId
      }
    });

    firstValueFrom(ref.afterClosed()).then((response) => {
      if (response && response.action === 'detail' && !!response.vehicle?.data?.id) {
        // response is 'detail' by default
        this.router.navigate([
          `/${RootRoute.SETTINGS}`, SettingsRoute.MANAGE_VEHICLES,
          'vehicle', response.vehicle.data.id]);
      } else if (response && response.action === 'list') {
        // response is a list
        this.toastService.long('Vehicle has been added');
        this.vehicleDataSource.reload();
      }
      // if response not set, just closed popup without any action
    });
  }

  deleteVehicle(vehicle: VehicleModel) {
    const dialogRef = this.dialog
      .open<DialogConfirmDeleteVehicleComponent, VehicleData>(DialogConfirmDeleteVehicleComponent, {
        width: '400px',
        // @ts-ignore
        data: vehicle as VehicleData
      });

    firstValueFrom(dialogRef.afterClosed()).then(result => {
      if (!result) {
        return;
      }
      this.vehicleService.deleteVehicle(vehicle.id).then(() => {
        this.vehicleDataSource.reload();
        this.toastService.long('Vehicle has been deleted.');
      });
    });
  }

  moveVehicle(vehicle: VehicleModel) {
    const dialogRef = this.dialog
      .open<DialogMoveVehicleComponent, VehicleModel>(DialogMoveVehicleComponent, {
        width: '400px',
        // @ts-ignore
        data: vehicle
      });

    firstValueFrom(dialogRef.afterClosed()).then(result => {
      if (!result) {
        return;
      }
      firstValueFrom(this.vehicleService.updateVehicle(vehicle.id, vehicle)).then(() => {
        this.vehicleDataSource.reload();
        this.toastService.long('Vehicle has been moved.');
      });
    });
  }

  notificationUsersChanged(user: { email: string; selected: boolean }, event: MatCheckboxChange) {
    user.selected = event.checked;
    this.validateRecipientSelection();
    this.formGroup.updateValueAndValidity();
  }

  private loadGeneralConfiguration() {
    this.subscriptions.push(
      this.configurationService.sharedConfigurationModel.subscribe(model => {
        this.configuration = model;
        this.isCartegraphEnabled = !!model?.featureFlags
          ?.find(flag => flag.name === FeatureFlagEnum.CartegraphIntegration && flag.isEnabled);
      })
    );
  }

  private async loadGroupData() {
    this.isLoadingGroup = true;
    this.vehicleGroup = await this.vehicleService.getVehicleCategory(this.groupId);

    try {
      const supervisorResponse = await this.userService.getSupervisorList();
      const activeUserEmails = (supervisorResponse?.data || [])
        .filter(user => !user.email.startsWith('neotreks_support'))
        .map(user => user.email);

      const configuredRecipients = this.vehicleGroup?.stationaryConfiguration?.recipientEmails || [];

      this.notificationUsers = activeUserEmails.map(email => ({
        email,
        selected: configuredRecipients.includes(email)
      }));

      this.invalidRecipients = configuredRecipients.filter(email => !activeUserEmails.includes(email));
    } catch (e) {
      this.notificationUsers = [];
      this.invalidRecipients = [];
    } finally {
      this.isLoadingGroup = false;
    }

      this.setFormValue(this.vehicleGroup);
  }

  get isEmailNotificationEnabled(): boolean {
    const configGroup = this.formGroup?.controls['stationaryConfiguration'] as FormGroup;
    const type = configGroup?.controls['notificationType']?.value;
    return type != null && type !== StationaryNotificationType.NONE;
  }

  onNotificationToggleChange(enabled: boolean) {
    const configGroup = this.formGroup?.controls['stationaryConfiguration'] as FormGroup;
    if (enabled) {
      const currentType = configGroup?.controls['notificationType']?.value;
      const nextType = (currentType && currentType !== StationaryNotificationType.NONE)
        ? currentType
        : StationaryNotificationType.LOGGED_IN_USERS;
      configGroup.controls['notificationType'].setValue(nextType);
    } else {
      configGroup.controls['notificationType'].setValue(StationaryNotificationType.NONE);
    }
  }

  private createFormGroup() {
    this.formGroup = this.fb.group<any>({
      stationaryAlertSet: this.fb.control(false),
      stationaryConfiguration: this.fb.group({
        maxStationaryTime: this.fb.control(null, [Validators.required]),
        notificationType: this.fb.control(StationaryNotificationType.LOGGED_IN_USERS),
      })
    });

    this.subscriptions.push(
      this.formGroup.controls['stationaryAlertSet'].valueChanges.subscribe(value => this.handleStationaryAlertSetChange(value))
    );

    this.subscriptions.push(
      this.formGroup.valueChanges.pipe(
        debounceTime(500),
        filter(() => {
          this.validateRecipientSelection();
          return this.formGroup.valid;
        }),
        switchMap(value => {
          this.isSaving = true;
          const {
            stationaryAlertSet,
            stationaryConfiguration,
          } = value;

          const selectedRecipients = this.notificationUsers
            .filter(u => u.selected)
            .map(u => u.email);

          const notificationType = stationaryConfiguration.notificationType || StationaryNotificationType.NONE;

          const stationaryConfig: VehicleGroupStationaryConfiguration = {
            maxStationaryTime: stationaryAlertSet ? stationaryConfiguration.maxStationaryTime : null,
            notificationType,
            recipientEmails: notificationType === StationaryNotificationType.SELECTED_RECIPIENTS ? selectedRecipients : []
          };

          const model: VehicleCategoryModel = {
            ...this.vehicleGroup,
            stationaryConfiguration: stationaryConfig
          };

          delete this.saveError;
          return this.vehicleService.updateVehicleCategory(this.groupId, model).pipe(
            catchError(error => {
              this.saveError = error;
              this.isSaving = false;
              return of();
            })
          );
        }),
        tap(response => {
          this.isSaving = false;
          this.vehicleGroup = response;
          // Clear invalidRecipients on successful save as stale emails are pruned
          this.invalidRecipients = [];
          this.setFormValue(this.vehicleGroup, false);
        }),
      ).subscribe()
    );
  }

  private validateRecipientSelection() {
    const stationaryAlertSet = this.formGroup.controls['stationaryAlertSet']?.value;
    const stationaryConfigGroup = this.formGroup.controls['stationaryConfiguration'] as FormGroup;
    const notificationType = stationaryConfigGroup?.controls['notificationType']?.value;

    if (stationaryAlertSet && notificationType === StationaryNotificationType.SELECTED_RECIPIENTS) {
      const hasRecipient = this.notificationUsers.some(user => user.selected);
      if (!hasRecipient) {
        this.formGroup.setErrors({ missingRecipient: true });
        return;
      }
    }
    if (this.formGroup.hasError('missingRecipient')) {
      const errors = { ...this.formGroup.errors };
      delete errors['missingRecipient'];
      this.formGroup.setErrors(Object.keys(errors).length ? errors : null);
    }
  }

  private setFormValue(value: VehicleCategoryModel, emitEvent = false) {
    const stationaryConfig = value.stationaryConfiguration || new VehicleGroupStationaryConfiguration();
    const maxStationaryTime = stationaryConfig.maxStationaryTime ?? null;
    const stationaryAlertSet = !!maxStationaryTime;

    const stationaryConfigGroup = this.formGroup.controls['stationaryConfiguration'] as FormGroup;
    const durationControl = stationaryConfigGroup.controls['maxStationaryTime'];

    if (!stationaryAlertSet) {
      durationControl.disable({emitEvent: false});
    } else {
      durationControl.enable({emitEvent: false});
    }

    const notificationType = stationaryConfig.notificationType || StationaryNotificationType.LOGGED_IN_USERS;

    this.formGroup.setValue({
      stationaryAlertSet,
      stationaryConfiguration: {
        maxStationaryTime,
        notificationType
      }
    }, {emitEvent});
  }

  private handleStationaryAlertSetChange(value: boolean) {
    const stationaryConfigGroup = this.formGroup.controls['stationaryConfiguration'] as FormGroup;
    const durationControl = stationaryConfigGroup.controls['maxStationaryTime'];

    if (value) {
      durationControl.enable({emitEvent: false});
      durationControl.setValue(STATIONARY_ALERT_LIST_OPTIONS[0].id, {emitEvent: false});
    } else {
      durationControl.disable({emitEvent: false});
    }
  }
}
